
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Calendar,
  LayoutDashboard,
  Menu,
  Scissors,
  Bell,
  Users,
  X,
  Link2,
  Settings,
  Clock4,
  MessageSquare,
  Palette
} from "lucide-react";
import { UserMenu } from "./UserMenu";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

export function DashboardNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const navigationItems = [
    { name: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { name: "Calendar", icon: Calendar, path: "/dashboard/calendar" },
    { name: "Services", icon: Scissors, path: "/dashboard/services" },
    { name: "Hours", icon: Clock4, path: "/dashboard/hours" },
    { name: "Notifications", icon: Bell, path: "/dashboard/notifications" },
    { name: "Clients", icon: Users, path: "/dashboard/clients" },
    { name: "Campaigns", icon: MessageSquare, path: "/dashboard/campaigns" },
    { name: "Link In Bio", icon: Link2, path: "/dashboard/linkinbio" },
    { name: "Booking Page", icon: Palette, path: "/dashboard/booking-page" },
    { name: "Business Settings", icon: Settings, path: "/dashboard/business-settings" },
  ];

  return (
    <>
      <div className="border-b bg-white sticky top-0 z-40">
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2 mr-6">
              <img
                src="/uploads/figgy-wordmark.png"
                alt="Figgy Logo"
                className="h-7 sm:h-8 w-auto max-w-[180px]"
              />
            </Link>
            
            {!isMobile && (
              <nav className="flex items-center gap-6 text-sm">
                {navigationItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors",
                      isActive(item.path) && "text-figgy-600 font-medium"
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.name}
                  </Link>
                ))}
              </nav>
            )}
            
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMenuOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
          </div>
          
          <UserMenu />
        </div>
      </div>
      
      {/* Mobile navigation menu */}
      {isMobile && (
        <div
          className={cn(
            "fixed inset-0 z-50 bg-white transform transition-transform duration-300 ease-in-out",
            menuOpen ? "translate-x-0 pointer-events-auto" : "-translate-x-full pointer-events-none"
          )}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
                <img
                  src="/uploads/figgy-wordmark.png"
                  alt="Figgy Logo"
                  className="h-7 w-auto max-w-[180px]"
                />
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setMenuOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <nav className="flex-1 overflow-auto p-4">
              <ul className="space-y-4">
                {navigationItems.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-md hover:bg-gray-100",
                        isActive(item.path) && "bg-figgy-50 text-figgy-600 font-medium"
                      )}
                      onClick={() => setMenuOpen(false)}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
